<template>
  <div class="codes-component">
    <!-- Generation form -->
    <div class="generate-section">
      <div class="section-header">
        <h2>Generate Codes</h2>
      </div>
      <p class="hint">
        One-time codes that grant gifted premium (months of <strong>premiumUntil</strong>). Hand
        them out as tournament prizes, giveaways, etc. Each code can be redeemed once, by one
        account, on the Account page. Gifted premium is Hero-level (no Warmaster early access), and
        awards redeemed by an active subscriber start when their paid period ends. Expiry is
        end-of-day UTC.
      </p>
      <form class="generate-form" @submit.prevent="generate">
        <label>
          Count
          <input v-model.number="genCount" type="number" min="1" max="500" required />
        </label>
        <label>
          Months
          <input v-model.number="genMonths" type="number" min="1" max="120" required />
        </label>
        <label class="note-field">
          Note (event / reason)
          <input v-model="genNote" type="text" placeholder="e.g. LVO 2026 — Best Destruction" />
        </label>
        <label>
          Expires (optional)
          <input v-model="genExpiresAt" type="date" />
        </label>
        <button type="submit" class="primary-btn" :disabled="generating">
          {{ generating ? 'Generating...' : 'Generate' }}
        </button>
      </form>
      <div v-if="generateError" class="error">{{ generateError }}</div>

      <div v-if="freshCodes.length > 0" class="fresh-codes">
        <div class="fresh-codes-header">
          <h3>New codes ({{ freshCodes.length }})</h3>
          <button class="copy-btn" @click="copyText(freshCodes.join('\n'), 'fresh')">
            {{ copiedKey === 'fresh' ? 'Copied!' : 'Copy all' }}
          </button>
        </div>
        <pre class="codes-block">{{ freshCodes.join('\n') }}</pre>
      </div>
    </div>

    <!-- Code list -->
    <div class="list-section">
      <div class="section-header">
        <h2>All Codes</h2>
        <div class="header-controls">
          <select v-model="statusFilter" class="status-select">
            <option value="all">All ({{ codes.length }})</option>
            <option value="available">Available ({{ availableCodes.length }})</option>
            <option value="redeemed">Redeemed ({{ redeemedCount }})</option>
            <option value="expired">Expired ({{ expiredCount }})</option>
          </select>
          <div class="search-container">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search codes/notes/users..."
              class="search-input"
            />
          </div>
          <button
            v-if="availableCodes.length > 0"
            class="copy-btn"
            @click="copyText(availableCodes.map((c) => c.code).join('\n'), 'available')"
          >
            {{ copiedKey === 'available' ? 'Copied!' : 'Copy available' }}
          </button>
          <button :disabled="loading" class="refresh-btn" @click="loadCodes">
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>
        </div>
      </div>

      <div v-if="loading && codes.length === 0" class="loading">Loading codes...</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="filteredCodes.length === 0" class="empty">No codes found.</div>
      <div v-else class="table-container">
        <table class="codes-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Months</th>
              <th>Note</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Status</th>
              <th class="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="code in filteredCodes" :key="code.id">
              <td class="code-cell">
                <code>{{ code.code }}</code>
              </td>
              <td>{{ code.months }}</td>
              <td class="note-cell">{{ code.note || '—' }}</td>
              <td>{{ formatDate(code.createdAt) }}</td>
              <td>{{ code.expiresAt ? formatDateUTC(code.expiresAt) : '—' }}</td>
              <td>
                <span :class="['status-pill', statusOf(code)]">
                  {{ statusLabel(code) }}
                </span>
                <span v-if="code.redeemedBy" class="redeemer">
                  {{ code.redeemedBy.username }}
                </span>
              </td>
              <td class="actions-column">
                <button class="copy-btn small" @click="copyText(code.code, code.id)">
                  {{ copiedKey === code.id ? 'Copied!' : 'Copy' }}
                </button>
                <button
                  v-if="!code.redeemedAt"
                  :class="['revoke-btn', { arming: revokeArmedId === code.id }]"
                  @click="revoke(code)"
                >
                  {{ revokeArmedId === code.id ? 'Confirm?' : 'Revoke' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { client, parseErrorMessage, type RedemptionCode } from '../client.js';

const codes = ref<RedemptionCode[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const searchQuery = ref('');
const statusFilter = ref<'all' | 'available' | 'redeemed' | 'expired'>('all');

const generating = ref(false);
const generateError = ref<string | null>(null);
const genCount = ref(1);
const genMonths = ref(6);
const genNote = ref('');
const genExpiresAt = ref('');
const freshCodes = ref<string[]>([]);

const copiedKey = ref<string | null>(null);
const revokeArmedId = ref<string | null>(null);

function statusOf(code: RedemptionCode): 'available' | 'redeemed' | 'expired' {
  if (code.redeemedAt) return 'redeemed';
  if (code.expiresAt && code.expiresAt.getTime() <= Date.now()) return 'expired';
  return 'available';
}

function statusLabel(code: RedemptionCode): string {
  const status = statusOf(code);
  if (status === 'redeemed') return `Redeemed ${formatDate(code.redeemedAt!)}`;
  return status === 'expired' ? 'Expired' : 'Available';
}

const availableCodes = computed(() => codes.value.filter((c) => statusOf(c) === 'available'));
const redeemedCount = computed(() => codes.value.filter((c) => statusOf(c) === 'redeemed').length);
const expiredCount = computed(() => codes.value.filter((c) => statusOf(c) === 'expired').length);

const filteredCodes = computed(() => {
  let list = codes.value;
  if (statusFilter.value !== 'all') {
    list = list.filter((c) => statusOf(c) === statusFilter.value);
  }
  const query = searchQuery.value.toLowerCase().trim();
  if (!query) return list;
  return list.filter((c) =>
    [c.code, c.note, c.redeemedBy?.username ?? '', c.redeemedBy?.email ?? '']
      .join(' ')
      .toLowerCase()
      .includes(query),
  );
});

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Expiry is stored as end-of-day UTC; render it in UTC so the date shown
// matches the date the admin typed, regardless of their timezone.
function formatDateUTC(date: Date): string {
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

async function loadCodes() {
  if (!client.value) return;
  loading.value = true;
  error.value = null;
  try {
    codes.value = await client.value.getRedemptionCodes();
  } catch (err) {
    error.value = parseErrorMessage(err);
  } finally {
    loading.value = false;
  }
}

async function generate() {
  if (!client.value) return;
  generating.value = true;
  generateError.value = null;
  try {
    freshCodes.value = await client.value.generateRedemptionCodes({
      count: genCount.value,
      months: genMonths.value,
      note: genNote.value || undefined,
      // The date input yields YYYY-MM-DD; treat it as end-of-day UTC so a code
      // "expiring Aug 15" still works throughout Aug 15.
      expiresAt: genExpiresAt.value ? `${genExpiresAt.value}T23:59:59Z` : undefined,
    });
    await loadCodes();
  } catch (err) {
    generateError.value = parseErrorMessage(err);
  } finally {
    generating.value = false;
  }
}

// Two-click confirm (no window.confirm — it blocks and is easy to misclick past)
async function revoke(code: RedemptionCode) {
  if (revokeArmedId.value !== code.id) {
    revokeArmedId.value = code.id;
    setTimeout(() => {
      if (revokeArmedId.value === code.id) revokeArmedId.value = null;
    }, 4000);
    return;
  }
  revokeArmedId.value = null;
  if (!client.value) return;
  try {
    await client.value.revokeRedemptionCode(code.id);
    codes.value = codes.value.filter((c) => c.id !== code.id);
  } catch (err) {
    error.value = parseErrorMessage(err);
  }
}

async function copyText(text: string, key: string) {
  try {
    await navigator.clipboard.writeText(text);
    copiedKey.value = key;
    setTimeout(() => {
      if (copiedKey.value === key) copiedKey.value = null;
    }, 1500);
  } catch {
    error.value = 'Failed to copy to clipboard';
  }
}

onMounted(loadCodes);
</script>

<style scoped>
.codes-component {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.generate-section,
.list-section {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.section-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #111827;
}

.hint {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0 0 1rem;
}

.generate-form {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
}

.generate-form label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.generate-form input {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 8rem;
}

.generate-form .note-field {
  flex: 1;
  min-width: 14rem;
}

.generate-form .note-field input {
  width: 100%;
}

.primary-btn {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
}

.primary-btn:hover:not(:disabled) {
  background: #2563eb;
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.fresh-codes {
  margin-top: 1rem;
  border: 1px solid #bbf7d0;
  background: #f0fdf4;
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;
}

.fresh-codes-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.fresh-codes-header h3 {
  margin: 0;
  font-size: 0.95rem;
  color: #166534;
}

.codes-block {
  margin: 0.5rem 0 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85rem;
  white-space: pre-wrap;
  word-break: break-all;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.status-select,
.search-input {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.refresh-btn,
.copy-btn {
  padding: 0.5rem 0.75rem;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
}

.refresh-btn:hover:not(:disabled),
.copy-btn:hover {
  background: #e5e7eb;
}

.copy-btn.small {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

.revoke-btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  background: white;
  color: #dc2626;
  border: 1px solid #fca5a5;
  border-radius: 0.375rem;
  cursor: pointer;
  margin-left: 0.25rem;
}

.revoke-btn:hover,
.revoke-btn.arming {
  background: #dc2626;
  border-color: #dc2626;
  color: white;
}

.table-container {
  overflow-x: auto;
}

.codes-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.codes-table th {
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-bottom: 2px solid #e5e7eb;
  color: #6b7280;
  font-weight: 600;
  white-space: nowrap;
}

.codes-table td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  color: #111827;
  vertical-align: middle;
}

.code-cell code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  background: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  white-space: nowrap;
}

.note-cell {
  max-width: 16rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.actions-column {
  white-space: nowrap;
}

.status-pill {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-pill.available {
  background: #dcfce7;
  color: #166534;
}

.status-pill.redeemed {
  background: #dbeafe;
  color: #1e40af;
}

.status-pill.expired {
  background: #fee2e2;
  color: #991b1b;
}

.redeemer {
  margin-left: 0.375rem;
  color: #6b7280;
  font-size: 0.75rem;
}

.loading,
.empty {
  color: #6b7280;
  padding: 1.5rem 0;
  text-align: center;
}

.error {
  color: #dc2626;
  padding: 0.75rem 0;
}
</style>
